<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const workspaces = ref([]);
const workflows = ref([]);
const workflowInboxes = ref([]);
const selectedWorkspaceId = ref(null);
const selectedWorkflowId = ref(null);
const isLoadingWorkspaces = ref(false);
const isLoadingWorkflows = ref(false);
const isLoadingConfig = ref(false);
const isSaving = ref(false);
const enableConversationWorkflow = ref(false);

const hasAgentStudioConfig = computed(() => {
  return !!window.chatwootConfig?.agentStudioEnabled;
});

const selectedWorkflow = computed(() => {
  if (!selectedWorkflowId.value) return null;
  return workflows.value.find(w => w.id === selectedWorkflowId.value);
});

const fetchWorkspaces = async () => {
  isLoadingWorkspaces.value = true;
  try {
    const response = await store.dispatch('agentStudio/fetchWorkspaces');
    workspaces.value = response || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.WORKFLOWS.FETCH_WORKSPACES_ERROR'));
  } finally {
    isLoadingWorkspaces.value = false;
  }
};

const fetchWorkflows = async (workspaceId = null) => {
  isLoadingWorkflows.value = true;
  try {
    const response = await store.dispatch(
      'agentStudio/fetchWorkflows',
      workspaceId
    );
    workflows.value = response || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.WORKFLOWS.FETCH_ERROR'));
  } finally {
    isLoadingWorkflows.value = false;
  }
};

const fetchWorkflowInboxes = async () => {
  try {
    const response = await store.dispatch('inboxes/fetchWorkflowInboxes', {
      inboxId: props.inbox.id,
    });
    workflowInboxes.value = response || [];

    // Set the first agent studio workflow as selected
    const agentStudioWorkflow = workflowInboxes.value.find(
      wi => wi.agent_studio_workflow_id
    );
    if (agentStudioWorkflow) {
      selectedWorkflowId.value = agentStudioWorkflow.agent_studio_workflow_id;
    }

    return agentStudioWorkflow;
  } catch (error) {
    // Error is expected if no workflow inboxes exist yet
    return null;
  }
};

const loadExistingConfiguration = async () => {
  isLoadingConfig.value = true;
  try {
    const agentStudioWorkflow = await fetchWorkflowInboxes();

    if (agentStudioWorkflow?.agent_studio_workflow_id) {
      // Fetch the workflow details to get its workspace
      const workflow = await store.dispatch(
        'agentStudio/fetchWorkflow',
        agentStudioWorkflow.agent_studio_workflow_id
      );

      if (workflow?.workspaceId) {
        // Set the workspace and fetch workflows for that workspace
        selectedWorkspaceId.value = workflow.workspaceId;
        await fetchWorkflows(workflow.workspaceId);

        // Ensure the workflow is selected after loading
        selectedWorkflowId.value = agentStudioWorkflow.agent_studio_workflow_id;
      }
    }
  } catch {
    // If we can't load the existing configuration, just continue
    // The user can select a new workflow
  } finally {
    isLoadingConfig.value = false;
  }
};

const updateInboxWorkflowSettings = async (silent = false) => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      enable_conversation_workflow: enableConversationWorkflow.value,
    });
    if (!silent) {
      useAlert(t('INBOX_MGMT.WORKFLOWS.UPDATE_SUCCESS'));
    }
  } catch (error) {
    useAlert(t('INBOX_MGMT.WORKFLOWS.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveWorkflowInbox = async () => {
  if (!selectedWorkflowId.value) {
    useAlert(t('INBOX_MGMT.WORKFLOWS.SELECT_WORKFLOW_ERROR'));
    return;
  }

  isSaving.value = true;
  try {
    // Remove existing agent studio workflow associations
    const existingAgentStudioWorkflows = workflowInboxes.value.filter(
      wi => wi.agent_studio_workflow_id
    );

    const deletePromises = existingAgentStudioWorkflows.map(wi =>
      store.dispatch('inboxes/deleteWorkflowInbox', {
        inboxId: props.inbox.id,
        workflowInboxId: wi.id,
      })
    );
    await Promise.all(deletePromises);

    // Create new association
    await store.dispatch('inboxes/createWorkflowInbox', {
      inboxId: props.inbox.id,
      workflowInbox: {
        agent_studio_workflow_id: selectedWorkflowId.value,
      },
    });

    await fetchWorkflowInboxes();
    await updateInboxWorkflowSettings(true);

    useAlert(t('INBOX_MGMT.WORKFLOWS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.WORKFLOWS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleWorkspaceChange = async () => {
  // Reset workflow selection when workspace changes
  selectedWorkflowId.value = null;

  // Fetch workflows filtered by selected workspace
  if (selectedWorkspaceId.value) {
    await fetchWorkflows(selectedWorkspaceId.value);
  } else {
    workflows.value = [];
  }
};

onMounted(async () => {
  enableConversationWorkflow.value =
    props.inbox.enable_conversation_workflow || false;

  // First load workspaces
  await fetchWorkspaces();

  // Then load existing configuration (which will also set workspace and workflows)
  await loadExistingConfiguration();
});
</script>

<template>
  <div class="mx-8 space-y-6">
    <SettingsSection
      v-if="!hasAgentStudioConfig"
      :title="$t('INBOX_MGMT.WORKFLOWS.TITLE')"
      :sub-title="$t('INBOX_MGMT.WORKFLOWS.NOT_CONFIGURED')"
    >
      <div
        class="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
      >
        <p class="text-sm text-yellow-800 dark:text-yellow-200">
          {{ $t('INBOX_MGMT.WORKFLOWS.CONFIGURATION_REQUIRED') }}
        </p>
      </div>
    </SettingsSection>

    <template v-else>
      <SettingsSection
        :title="$t('INBOX_MGMT.WORKFLOWS.ENABLE_TITLE')"
        :sub-title="$t('INBOX_MGMT.WORKFLOWS.ENABLE_SUBTITLE')"
      >
        <div class="flex gap-2 items-center">
          <input
            id="enableConversationWorkflow"
            v-model="enableConversationWorkflow"
            type="checkbox"
            class="rounded border-slate-300 text-woot-600 focus:ring-woot-500"
          />
          <label
            for="enableConversationWorkflow"
            class="text-sm text-n-slate-12"
          >
            {{ $t('INBOX_MGMT.WORKFLOWS.ENABLE_LABEL') }}
          </label>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.WORKFLOWS.SELECT_TITLE')"
        :sub-title="$t('INBOX_MGMT.WORKFLOWS.SELECT_SUBTITLE')"
      >
        <!-- Loading existing configuration -->
        <div
          v-if="isLoadingConfig"
          class="flex items-center gap-2 p-4 bg-n-alpha-3 border border-n-weak rounded-lg"
        >
          <Spinner :size="16" class="text-n-slate-11" />
          <span class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.WORKFLOWS.LOADING_CONFIG') }}
          </span>
        </div>

        <!-- Workspace Selection -->
        <div v-else class="space-y-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WORKFLOWS.SELECT_WORKSPACE_LABEL') }}
            </label>

            <div
              v-if="isLoadingWorkspaces"
              class="flex items-center gap-2 p-3 bg-n-alpha-3 border border-n-weak rounded-lg"
            >
              <Spinner :size="16" class="text-n-slate-11" />
              <span class="text-sm text-n-slate-11">
                {{ $t('INBOX_MGMT.WORKFLOWS.LOADING') }}
              </span>
            </div>

            <div
              v-else-if="workspaces.length === 0"
              class="p-4 bg-n-alpha-3 border border-n-weak rounded-lg"
            >
              <p class="text-sm text-n-slate-11">
                {{ $t('INBOX_MGMT.WORKFLOWS.NO_WORKSPACES') }}
              </p>
            </div>

            <select
              v-else
              v-model="selectedWorkspaceId"
              class="w-full py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500 bg-n-alpha-1 text-n-slate-12"
              @change="handleWorkspaceChange"
            >
              <option :value="null">
                {{ $t('INBOX_MGMT.WORKFLOWS.SELECT_WORKSPACE_PLACEHOLDER') }}
              </option>
              <option
                v-for="workspace in workspaces"
                :key="workspace.id"
                :value="workspace.id"
              >
                {{ workspace.name }}
              </option>
            </select>
          </div>

          <!-- Workflow Selection -->
          <div v-if="selectedWorkspaceId" class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WORKFLOWS.SELECT_WORKFLOW_LABEL') }}
            </label>

            <div
              v-if="isLoadingWorkflows"
              class="flex items-center gap-2 p-3 bg-n-alpha-3 border border-n-weak rounded-lg"
            >
              <Spinner :size="16" class="text-n-slate-11" />
              <span class="text-sm text-n-slate-11">
                {{ $t('INBOX_MGMT.WORKFLOWS.LOADING') }}
              </span>
            </div>

            <div
              v-else-if="workflows.length === 0"
              class="p-4 bg-n-alpha-3 border border-n-weak rounded-lg"
            >
              <p class="text-sm text-n-slate-11">
                {{ $t('INBOX_MGMT.WORKFLOWS.NO_WORKFLOWS_IN_WORKSPACE') }}
              </p>
            </div>

            <select
              v-else
              v-model="selectedWorkflowId"
              class="w-full py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500 bg-n-alpha-1 text-n-slate-12"
            >
              <option :value="null">
                {{ $t('INBOX_MGMT.WORKFLOWS.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="workflow in workflows"
                :key="workflow.id"
                :value="workflow.id"
              >
                {{ workflow.name }}
              </option>
            </select>
          </div>

          <div
            v-if="selectedWorkflow"
            class="p-4 bg-n-alpha-3 border border-n-weak rounded-lg"
          >
            <h4 class="text-sm font-medium text-n-slate-12 mb-2">
              {{ selectedWorkflow.name }}
            </h4>
            <p
              v-if="selectedWorkflow.description"
              class="text-sm text-n-slate-11"
            >
              {{ selectedWorkflow.description }}
            </p>
            <div class="mt-2 flex items-center gap-2 text-xs text-n-slate-11">
              <span class="px-2 py-1 bg-n-alpha-5 rounded">
                {{ $t('INBOX_MGMT.WORKFLOWS.WORKFLOW_ID') }}
                {{ selectedWorkflow.id }}
              </span>
            </div>
          </div>

          <div v-if="selectedWorkspaceId" class="flex justify-start">
            <NextButton
              :disabled="!selectedWorkflowId || isSaving"
              @click="saveWorkflowInbox"
            >
              <span v-if="isSaving" class="flex items-center gap-2">
                <Spinner :size="16" class="text-white" />
                {{ $t('INBOX_MGMT.WORKFLOWS.SAVING') }}
              </span>
              <span v-else>
                {{ $t('INBOX_MGMT.WORKFLOWS.SAVE_BUTTON') }}
              </span>
            </NextButton>
          </div>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
